/**
 * Raid Team Build PIXI Overlay
 *
 * Lightweight HTML overlay for the draft phase, displayed over the PIXI scene.
 * Shows:
 * - Timer/header at top
 * - Compact roster grid at bottom (~25% of screen)
 * - Triggers pokemon spawn in PIXI scene when selections change
 */

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useRaid, RaidLobbyPlayer } from "@/providers/raid-provider";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { FloatingItemsButton } from "@/components/raid/floating-items-button";
import {
  RaidItemsModal,
  ItemTargetingState,
} from "@/components/raid/raid-items-modal";
import {
  ItemUseToastContainer,
  ItemUseToastData,
} from "@/components/ui/toast/item-use-toast";
import { Direction } from "types/battle-engine/battle";
import { RAID_ITEM_EFFECTS } from "types/raid-dynamodb";
import { PlayerRole, RaidPokemon, SlotConfig } from "types/raid-dynamodb";
import classNames from "classnames";
import { isXsScreen } from "@/utils/screen-size-check";
import { RoomSelectionScene } from "@/lib/raid/room-selection-scene";
import { PokemonWithSlot } from "@/lib/raid/room-section";

// ============================================================================
// Constants
// ============================================================================

const PLAYER_COLORS: Record<
  PlayerRole,
  { bg: string; border: string; text: string }
> = {
  player_a: {
    bg: "bg-pk-blue-1",
    border: "border-pk-blue-3",
    text: "text-pk-blue-5",
  },
  player_b: {
    bg: "bg-pk-red-1",
    border: "border-pk-red-3",
    text: "text-pk-red-5",
  },
  player_c: {
    bg: "bg-pk-green-1",
    border: "border-pk-green-3",
    text: "text-pk-green-5",
  },
  player_d: {
    bg: "bg-pk-yellow-1",
    border: "border-pk-yellow-3",
    text: "text-pk-yellow-5",
  },
};

// ============================================================================
// Hook for Draft State (reused from team build view)
// ============================================================================

function useRaidDraftPixi() {
  const {
    raidState,
    pickPokemon,
    useItem,
    usableItems,
    isLoadingItems,
    itemUseEvents,
    clearItemUseEvent,
  } = useRaid();

  const draft = raidState.draft;
  const players = raidState.players;
  const yourRole = raidState.your_role;
  const currentRound = raidState.current_round;
  const isBossRound = raidState.is_boss_round;
  const raidName = raidState.raid_name ?? "Raid";
  const slots = draft?.slots || [];
  const faintedPokemon = raidState.fainted_pokemon;
  const pokemonHp = raidState.pokemon_hp;

  // Map available pokemon by ID for easy lookup
  // Note: Depend on draft.available_pokemon (stable array), NOT the entire draft object
  // which changes every 100ms due to timer updates.
  const availablePokemon = draft?.available_pokemon;
  const availablePokemonMap = useMemo(() => {
    if (!availablePokemon) return new Map<number, RaidPokemon>();
    const map = new Map<number, RaidPokemon>();
    availablePokemon.forEach((p) => map.set(p.id, p));
    return map;
  }, [availablePokemon]);

  // Get your pokemon roster
  // Note: Use availablePokemon (stable), not draft which changes every 100ms
  const yourPokemon = useMemo(() => {
    if (!availablePokemon || !yourRole) return [];
    const yourPlayer = players[yourRole];
    if (!yourPlayer) return [];

    return availablePokemon.filter((p) => p.owner_id === yourPlayer.id);
  }, [availablePokemon, yourRole, players]);

  // Get selected pokemon with their slot indices for correct positioning
  // Note: Do NOT depend on 'draft' here - it changes every 100ms due to timer updates.
  // We only need 'slots' and 'availablePokemonMap' which are derived from draft.
  const selectedPokemonWithSlots = useMemo(() => {
    const selected: PokemonWithSlot[] = [];
    for (const slot of slots) {
      if (slot.pokemon_id) {
        const pokemon = availablePokemonMap.get(slot.pokemon_id);
        if (pokemon) {
          selected.push({ pokemon, slotIndex: slot.slot_index });
        }
      }
    }
    return selected;
  }, [slots, availablePokemonMap]);

  return {
    draft,
    players,
    yourRole,
    currentRound,
    isBossRound,
    raidName,
    slots,
    availablePokemonMap,
    yourPokemon,
    selectedPokemonWithSlots,
    pickPokemon,
    useItem,
    usableItems,
    isLoadingItems,
    faintedPokemon,
    pokemonHp,
    itemUseEvents,
    clearItemUseEvent,
  };
}

// ============================================================================
// Main Overlay Component
// ============================================================================

export interface RaidTeamBuildPixiOverlayProps {
  roomSelectionScene: RoomSelectionScene | null;
  isVisible: boolean;
}

export const RaidTeamBuildPixiOverlay = ({
  roomSelectionScene,
  isVisible,
}: RaidTeamBuildPixiOverlayProps) => {
  const {
    draft,
    players,
    yourRole,
    currentRound,
    isBossRound,
    raidName,
    slots,
    availablePokemonMap,
    yourPokemon,
    selectedPokemonWithSlots,
    pickPokemon,
    useItem,
    usableItems,
    isLoadingItems,
    faintedPokemon,
    pokemonHp,
    itemUseEvents,
    clearItemUseEvent,
  } = useRaidDraftPixi();

  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [itemTargeting, setItemTargeting] = useState<ItemTargetingState>({
    selectedItem: null,
    validTargetIds: [],
    isDragging: false,
    dragPosition: null,
  });

  // Total usable items count for badge
  const totalUsableItems = useMemo(
    () => usableItems.reduce((sum, item) => sum + item.amount, 0),
    [usableItems],
  );

  // Get all pokemon from all players for item targeting
  const allPokemon = useMemo(() => {
    return draft?.available_pokemon || [];
  }, [draft?.available_pokemon]);

  // Convert item use events to toast data
  const toasts: ItemUseToastData[] = useMemo(
    () =>
      itemUseEvents.map((event) => {
        const itemEffect =
          RAID_ITEM_EFFECTS[event.itemName as keyof typeof RAID_ITEM_EFFECTS];
        return {
          id: event.id,
          playerName: event.playerName,
          itemName: event.itemName,
          pokemonName: event.targetPokemonName,
          isHeal: itemEffect?.type === "heal",
        };
      }),
    [itemUseEvents],
  );

  // Update PIXI scene slot placeholders when slots or players change
  useEffect(() => {
    if (!roomSelectionScene) return;
    roomSelectionScene.updateSlotPlaceholders(slots, players);
  }, [roomSelectionScene, slots, players]);

  // Update PIXI scene when selected pokemon change (with slot info for correct positioning)
  useEffect(() => {
    if (!roomSelectionScene) return;
    // Skip updates when draft has ended (slots cleared) to preserve spawned pokemon for battle
    if (slots.length === 0) return;
    roomSelectionScene.updatePlayerPokemonWithSlots(
      selectedPokemonWithSlots,
      slots.length,
    );
  }, [roomSelectionScene, selectedPokemonWithSlots, slots.length]);

  // Track processed item events to avoid duplicate VFX
  const processedItemEvents = useRef<Set<string>>(new Set());

  // Handle item use events - trigger PIXI VFX
  useEffect(() => {
    if (!roomSelectionScene || itemUseEvents.length === 0) return;

    for (const event of itemUseEvents) {
      // Skip already processed events
      if (processedItemEvents.current.has(event.id)) continue;
      processedItemEvents.current.add(event.id);

      // Get heal amount from item effect
      const itemEffect =
        RAID_ITEM_EFFECTS[event.itemName as keyof typeof RAID_ITEM_EFFECTS];
      const healAmount = itemEffect?.value ?? 0;

      // Trigger VFX on the PIXI scene
      roomSelectionScene.applyItemEffect(
        event.targetPokemonId,
        event.itemName,
        healAmount === Infinity ? 9999 : healAmount,
        event.newHp ?? 0,
      );
    }
  }, [roomSelectionScene, itemUseEvents]);

  // Show/hide PIXI targeting indicators when targeting state changes
  useEffect(() => {
    if (!roomSelectionScene) return;

    if (itemTargeting.selectedItem && itemTargeting.validTargetIds.length > 0) {
      roomSelectionScene.showTargetingIndicators(itemTargeting.validTargetIds);
    } else {
      roomSelectionScene.clearTargetingIndicators();
    }

    return () => {
      roomSelectionScene.clearTargetingIndicators();
    };
  }, [roomSelectionScene, itemTargeting.selectedItem, itemTargeting.validTargetIds]);

  // Handle clicks on PIXI canvas for item targeting
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (!roomSelectionScene || !itemTargeting.selectedItem) return;

      const pokemonId = roomSelectionScene.getPokemonAtPosition(
        e.clientX,
        e.clientY,
        itemTargeting.validTargetIds,
      );

      if (pokemonId !== null) {
        const applyFn = (window as any).__raidItemApply;
        if (applyFn) applyFn(pokemonId);
      }
    },
    [roomSelectionScene, itemTargeting.selectedItem, itemTargeting.validTargetIds],
  );

  // Handle drag drop on PIXI canvas
  useEffect(() => {
    if (!roomSelectionScene || !itemTargeting.isDragging) return;

    const handleDrop = () => {
      if (!itemTargeting.dragPosition) return;

      const pokemonId = roomSelectionScene.getPokemonAtPosition(
        itemTargeting.dragPosition.x,
        itemTargeting.dragPosition.y,
        itemTargeting.validTargetIds,
      );

      if (pokemonId !== null) {
        const applyFn = (window as any).__raidItemApply;
        if (applyFn) applyFn(pokemonId);
      }
    };

    window.addEventListener("mouseup", handleDrop, { once: true });
    window.addEventListener("touchend", handleDrop, { once: true });

    return () => {
      window.removeEventListener("mouseup", handleDrop);
      window.removeEventListener("touchend", handleDrop);
    };
  }, [roomSelectionScene, itemTargeting.isDragging, itemTargeting.dragPosition, itemTargeting.validTargetIds]);

  /**
   * Handles pokemon selection with simplified logic:
   * - If pokemon is already selected by you, de-select it
   * - Otherwise, place in first available slot (personal yours or shared)
   * - If no available slot, replace first personal slot you own
   */
  const handlePick = useCallback(
    (pokemonId: number) => {
      // Check if this pokemon is already selected
      const existingSlot = slots.find((s) => s.pokemon_id === pokemonId);

      if (existingSlot) {
        // Pokemon is already selected - check if it's ours to de-select
        if (existingSlot.picked_by === yourRole) {
          // De-select by picking with same pokemon (server handles toggle)
          pickPokemon(pokemonId, existingSlot.slot_index);
        }
        // If picked by another player, do nothing (can't de-select their picks)
        return;
      }

      // Find first available slot (empty personal yours or empty shared)
      const firstAvailable = slots.find(
        (s) =>
          s.pokemon_id === undefined &&
          ((s.type === "personal" && s.assigned_to === yourRole) ||
            s.type === "shared"),
      );

      if (firstAvailable) {
        pickPokemon(pokemonId, firstAvailable.slot_index);
        return;
      }

      // No available slot - replace first personal slot you own
      const firstPersonal = slots.find(
        (s) => s.type === "personal" && s.assigned_to === yourRole,
      );

      if (firstPersonal) {
        pickPokemon(pokemonId, firstPersonal.slot_index);
      }
    },
    [slots, yourRole, pickPokemon],
  );

  // Map picked pokemon IDs to their slot info
  // Must be before early return to maintain consistent hook order
  const pickedMap = useMemo(() => {
    const map = new Map<number, { slotIndex: number; pickedBy: PlayerRole }>();
    slots.forEach((s) => {
      if (s.pokemon_id && s.picked_by) {
        map.set(s.pokemon_id, {
          slotIndex: s.slot_index,
          pickedBy: s.picked_by,
        });
      }
    });
    return map;
  }, [slots]);

  if (!draft) {
    return null;
  }

  const timeRemaining = draft.time_remaining;
  const isUrgent = timeRemaining <= 10;
  const isCritical = timeRemaining <= 5;

  const filledSlotsCount = slots.filter(
    (s) => s.pokemon_id !== undefined,
  ).length;

  const colors = yourRole ? PLAYER_COLORS[yourRole] : PLAYER_COLORS.player_a;

  return (
    <div
      className={classNames(
        "absolute inset-0 flex flex-col pointer-events-none transition-opacity duration-200",
        {
          "opacity-100": isVisible,
          "opacity-0": !isVisible,
        },
      )}
    >
      {/* Canvas click overlay for PIXI pokemon targeting */}
      {itemTargeting.selectedItem && (
        <div
          className="absolute inset-0 z-10 cursor-crosshair"
          onClick={handleCanvasClick}
          style={{ pointerEvents: "auto" }}
        />
      )}

      {/* Header - Timer and Round Info */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-auto p-2">
        <DraftHeader
          raidName={raidName}
          currentRound={currentRound}
          isBossRound={isBossRound}
          timeRemaining={timeRemaining}
          isUrgent={isUrgent}
          isCritical={isCritical}
          filledSlotsCount={filledSlotsCount}
          totalSlots={slots.length}
        />
      </div>

      {/* Spacer to push roster to bottom */}
      <div className="flex-1" />

      {/* Floating Items Button - positioned above the roster */}
      <div className="absolute right-3 bottom-[180px] z-30 pointer-events-auto">
        <FloatingItemsButton
          totalItems={totalUsableItems}
          onClick={() => setIsItemsModalOpen(true)}
          disabled={isLoadingItems}
        />
      </div>

      {/* Compact Roster at Bottom (~25% of screen) */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-auto">
        <CompactRoster
          pokemons={yourPokemon}
          pickedMap={pickedMap}
          yourRole={yourRole}
          colors={colors}
          onPick={handlePick}
          itemTargeting={itemTargeting}
          onItemTarget={(pokemonId) => {
            const applyFn = (window as any).__raidItemApply;
            if (applyFn) applyFn(pokemonId);
          }}
        />
      </div>

      {/* Item Use Toasts */}
      <ItemUseToastContainer toasts={toasts} onDismiss={clearItemUseEvent} />

      {/* Items Panel - rendered with fixed positioning, outside pointer-events-none flow */}
      <RaidItemsModal
        isOpen={isItemsModalOpen}
        onClose={() => setIsItemsModalOpen(false)}
        items={usableItems}
        allPokemon={allPokemon}
        faintedPokemonIds={faintedPokemon}
        pokemonHp={pokemonHp}
        onUseItem={useItem}
        onTargetingChange={setItemTargeting}
      />
    </div>
  );
};

// ============================================================================
// Draft Header Component
// ============================================================================

interface DraftHeaderProps {
  raidName: string;
  currentRound: number;
  isBossRound: boolean;
  timeRemaining: number;
  isUrgent: boolean;
  isCritical: boolean;
  filledSlotsCount: number;
  totalSlots: number;
}

const DraftHeader = ({
  raidName,
  currentRound,
  isBossRound,
  timeRemaining,
  isUrgent,
  isCritical,
  filledSlotsCount,
  totalSlots,
}: DraftHeaderProps) => {
  const timerVariant = isCritical ? "red" : isUrgent ? "orange" : "blue";
  const isComplete = filledSlotsCount >= totalSlots;

  return (
    <div className="bg-black/50 backdrop-blur-sm rounded-lg p-2">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h1 className="text-lg xs:text-xl font-black text-white tracking-tight leading-none drop-shadow-lg">
            {raidName}
          </h1>
          <div
            className={classNames(
              "px-2 py-0.5 rounded-full text-xs font-bold",
              isComplete
                ? "bg-pk-green-3/80 text-pk-green-5"
                : "bg-pk-blue-3/80 text-pk-blue-5",
            )}
          >
            {filledSlotsCount}/{totalSlots}
          </div>
        </div>
        <StrokedText
          fontSize={isXsScreen() ? 12 : 18}
          strokeWidth={6}
          strokeColor="#414141"
          fillColor="#f4eae7"
        >
          {isBossRound ? "BOSS" : `Room ${currentRound + 1}/10`}
        </StrokedText>
      </div>

      {/* Timer Bar */}
      <div className="relative">
        {timeRemaining <= 0 ? (
          <div className="flex items-center justify-end gap-2 h-full">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent shadow-sm" />
            <span className="text-white text-xs font-bold">Starting...</span>
          </div>
        ) : (
          <>
            <SegmentedProgressBar
              ratio={timeRemaining / 30}
              variant={timerVariant as "red" | "orange" | "blue"}
              maxSquares={30}
              segmented={false}
              className={classNames(
                "h-2 border-2 rounded-full overflow-hidden p-0",
                {
                  "animate-pulse": isCritical,
                },
              )}
            />
            <div className="absolute -top-0.5 right-0">
              <StrokedText
                fontSize={18}
                strokeWidth={8}
                strokeColor="#414141"
                fillColor="#f4eae7"
              >
                {String(Math.ceil(timeRemaining)).padStart(2, "0")}s
              </StrokedText>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Compact Roster Component
// ============================================================================

interface CompactRosterProps {
  pokemons: RaidPokemon[];
  pickedMap: Map<number, { slotIndex: number; pickedBy: PlayerRole }>;
  yourRole: PlayerRole | null;
  colors: { bg: string; border: string; text: string };
  onPick: (pokemonId: number) => void;
  itemTargeting: ItemTargetingState;
  onItemTarget: (pokemonId: number) => void;
}

const CompactRoster = ({
  pokemons,
  pickedMap,
  yourRole,
  colors,
  onPick,
  itemTargeting,
  onItemTarget,
}: CompactRosterProps) => {
  const rosterRef = useRef<HTMLDivElement>(null);

  // Handle drop detection for drag-drop
  useEffect(() => {
    if (!itemTargeting.isDragging || !itemTargeting.dragPosition || !rosterRef.current) return;

    const checkDropTarget = () => {
      const cards = rosterRef.current?.querySelectorAll("[data-pokemon-id]");
      if (!cards) return;

      for (const card of cards) {
        const rect = card.getBoundingClientRect();
        const { x, y } = itemTargeting.dragPosition!;

        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          const pokemonId = parseInt(card.getAttribute("data-pokemon-id") || "0", 10);
          if (itemTargeting.validTargetIds.includes(pokemonId)) {
            onItemTarget(pokemonId);
            break;
          }
        }
      }
    };

    // Check on drag end (mouseup/touchend handled in modal)
    const handleEnd = () => checkDropTarget();
    window.addEventListener("mouseup", handleEnd, { once: true });
    window.addEventListener("touchend", handleEnd, { once: true });

    return () => {
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [itemTargeting.isDragging, itemTargeting.dragPosition, itemTargeting.validTargetIds, onItemTarget]);

  return (
    <div ref={rosterRef} className="bg-pk-gray-2 border-t-4 border-pk-blue-4 shadow-2xl pb-safe">
      <div className="p-2">
        {/* Pokemon grid */}
        <div className="grid grid-cols-4 gap-0">
          {pokemons.map((pokemon) => {
            const pickedInfo = pickedMap.get(pokemon.id);
            const isYourPick = pickedInfo?.pickedBy === yourRole;
            const isFainted =
              pokemon.is_fainted ||
              (pokemon.current_hp !== undefined && pokemon.current_hp <= 0);
            const isValidTarget = itemTargeting.validTargetIds.includes(pokemon.id);
            const isTargetingActive = itemTargeting.selectedItem !== null;

            return (
              <CompactPokemonCard
                key={pokemon.id}
                pokemon={pokemon}
                isYourPick={isYourPick}
                isFainted={isFainted}
                ownerColors={colors}
                onToggle={() => {
                  // If targeting mode and valid target, apply item instead of pick
                  if (isTargetingActive && isValidTarget) {
                    onItemTarget(pokemon.id);
                  } else if (!isTargetingActive) {
                    onPick(pokemon.id);
                  }
                }}
                isValidTarget={isValidTarget}
                isTargetingActive={isTargetingActive}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Compact Pokemon Card Component
// ============================================================================

interface CompactPokemonCardProps {
  pokemon: RaidPokemon;
  isYourPick: boolean;
  isFainted: boolean;
  ownerColors: { bg: string; border: string; text: string };
  onToggle: () => void;
  isValidTarget?: boolean;
  isTargetingActive?: boolean;
}

const CompactPokemonCard = ({
  pokemon,
  isYourPick,
  isFainted,
  ownerColors,
  onToggle,
  isValidTarget = false,
  isTargetingActive = false,
}: CompactPokemonCardProps) => {
  const hpPercent =
    pokemon.max_hp && pokemon.current_hp !== undefined
      ? Math.max(0, Math.min(100, (pokemon.current_hp / pokemon.max_hp) * 100))
      : 100;

  // In targeting mode: valid targets are clickable, invalid are dimmed
  const isClickable = isTargetingActive ? isValidTarget : !isFainted;

  return (
    <button
      data-pokemon-id={pokemon.id}
      onClick={onToggle}
      disabled={!isClickable}
      className={classNames(
        "group relative flex flex-col items-center rounded-md overflow-visible transition-all duration-150",
        "bg-pk-gray-3 border-2 p-1",
        {
          // Normal mode styles
          "border-amber-400 bg-slate-700 shadow-[0_0_10px_rgba(251,191,36,0.3)] scale-105 z-10":
            isYourPick && !isTargetingActive,
          "border-pk-green-1 hover:border-slate-400 hover:bg-slate-700":
            !isYourPick && !isFainted && !isTargetingActive,
          "border-pk-beige-4 bg-slate-900 opacity-50 cursor-not-allowed grayscale":
            isFainted && !isTargetingActive,

          // Targeting mode styles
          "border-pk-green-4 bg-pk-green-1/20 shadow-[0_0_12px_rgba(74,222,128,0.5)] scale-105 z-10":
            isTargetingActive && isValidTarget,
          "opacity-40 grayscale pointer-events-none":
            isTargetingActive && !isValidTarget,

          "active:scale-95": isClickable,
        },
      )}
    >
      {/* Targeting VFX Overlay */}
      {isTargetingActive && isValidTarget && (
        <div className="absolute inset-0 rounded-md overflow-hidden pointer-events-none z-30">
          {/* Pulsing ring */}
          <div className="absolute inset-0 border-2 border-pk-green-4 rounded-md animate-pulse" />
          {/* Sparkle dots */}
          <div className="absolute top-0 left-1/2 w-1.5 h-1.5 bg-white rounded-full animate-ping" />
          <div
            className="absolute bottom-1 right-1 w-1 h-1 bg-pk-green-3 rounded-full animate-ping"
            style={{ animationDelay: "150ms" }}
          />
          <div
            className="absolute top-2 left-1 w-1 h-1 bg-pk-green-3 rounded-full animate-ping"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      )}

      {/* Pokemon Sprite - Compact */}
      <div className="relative w-10 h-10 flex items-center justify-center overflow-visible mb-1">
        <PokemonSprite
          pokemon={{ base_pokemon_name: pokemon.base_pokemon_name }}
          animation="Idle"
          direction={Direction.SE}
          className={classNames("w-full h-full object-contain scale-200", {
            grayscale: isFainted && !isTargetingActive,
          })}
          showShadow={!isFainted}
        />

        {isFainted && !isTargetingActive && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl drop-shadow-md">💀</span>
          </div>
        )}
      </div>

      {/* Level Badge */}
      <div className="absolute top-1 left-1 bg-pk-night-1/80 text-pk-gray-5 text-[9px] px-1 rounded font-mono font-bold leading-tight border border-white/10">
        Lv{pokemon.level}
      </div>

      {/* HP Bar - Custom Image Style */}
      {!isFainted && pokemon.max_hp && (
        <div className="relative w-[34px] h-[14px] -mt-1 shrink-0">
          {/* Background Frame */}
          <img
            src="/assets/ui/hp_bg.png"
            className="absolute inset-0 w-full h-full object-fill opacity-90"
            style={{ imageRendering: "pixelated" }}
            alt="hp"
          />

          {/* Fill Bar */}
          <div
            className={classNames(
              "absolute top-[3px] left-[4px] h-[8px] rounded-[1px] transition-all duration-300 bg-pk-green-4",
              ownerColors.bg.replace("bg-", "bg-"),
            )}
            style={{
              width: `${Math.max(0, (hpPercent / 100) * 27)}px`,
            }}
          />
        </div>
      )}

      {/* Selection Checkmark (only in normal mode) */}
      {isYourPick && !isTargetingActive && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-400 border-2 border-slate-900 rounded-full flex items-center justify-center shadow-sm z-20">
          <span className="text-amber-950 text-[10px] font-black leading-none">
            ✓
          </span>
        </div>
      )}

      {/* Target indicator (in targeting mode) */}
      {isTargetingActive && isValidTarget && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-pk-green-4 border-2 border-slate-900 rounded-full flex items-center justify-center shadow-sm z-20 animate-bounce">
          <span className="text-white text-[10px] font-black leading-none">
            +
          </span>
        </div>
      )}
    </button>
  );
};

export default RaidTeamBuildPixiOverlay;
